// Rolling Resistance Force Calculator tool: field metadata, the tyre-class x road-surface
// Crr lookup, and client-side field validation, used for instant inline feedback as the
// user types. The actual math, reference tables and the authoritative (server-side)
// validation live in the main backend — see src/lib/toolsApi.js
// (rollingResistanceToolApi) and backend/tools/rolling_resistance_tool.py.
// Mirrors backend/core/lookup/rolling_resistance_specs.py exactly (checked by
// backend/tests/test_rolling_resistance_tool.py).

export const DEFAULT_INPUTS = {
  vehicleMassKg: 1500,
  tyreClass: 'passenger',
  surface: 'smooth',
  rollingResistanceCoefficient: 0.010,
  gradePercent: 0,
  vehicleSpeedKmph: 100,
  drivetrainEfficiencyPct: 90,
  gravityMs2: 9.81,
};

export const FIELD_SPECS = [
  { key: 'vehicleMassKg', label: 'Vehicle Mass', unit: 'kg', min: 200, max: 60000, strictlyPositive: true },
  { key: 'gradePercent', label: 'Road Gradient', unit: '%', min: -35, max: 35, strictlyPositive: false },
  { key: 'vehicleSpeedKmph', label: 'Vehicle Speed', unit: 'km/h', min: 0, max: 250, strictlyPositive: false },
  { key: 'drivetrainEfficiencyPct', label: 'Drivetrain Efficiency', unit: '%', min: 50, max: 100, strictlyPositive: true },
  { key: 'gravityMs2', label: 'Gravity', unit: 'm/s²', min: 9.00, max: 10.00, strictlyPositive: true },
];

// Only validated when tyreClass === 'custom'.
export const CUSTOM_CRR_SPEC = { key: 'rollingResistanceCoefficient', label: 'Rolling Resistance Coefficient', unit: 'Crr', min: 0.002, max: 0.30, strictlyPositive: true };

// Base Crr on smooth, dry asphalt/concrete per tyre class (representative, inside the
// EU tyre-label band for each class).
export const TYRE_CLASSES = [
  { key: 'lrr', label: 'Passenger, low-RR / EV', baseCrr: 0.0070 },
  { key: 'passenger', label: 'Passenger, standard', baseCrr: 0.0100 },
  { key: 'van', label: 'Van / light truck (C2)', baseCrr: 0.0085 },
  { key: 'truck', label: 'Truck / bus (C3)', baseCrr: 0.0055 },
  { key: 'custom', label: 'Custom Crr', baseCrr: null },
];

// Paved surfaces scale the tyre's base Crr; loose/deformable surfaces use one absolute
// Crr whatever the tyre.
export const SURFACES = [
  { key: 'smooth', label: 'Smooth asphalt / concrete', factor: 1.00, crr: null },
  { key: 'wet', label: 'Wet asphalt', factor: 1.15, crr: null },
  { key: 'sett', label: 'Cobblestone / sett paving', factor: 1.20, crr: null },
  { key: 'gravel', label: 'Compacted gravel', factor: null, crr: 0.020 },
  { key: 'snow', label: 'Packed snow', factor: null, crr: 0.030 },
  { key: 'dirt', label: 'Unpaved / dirt road', factor: null, crr: 0.050 },
  { key: 'sand', label: 'Loose sand / deep snow', factor: null, crr: 0.150 },
];

// Preview of the Crr the backend will use, shown in the (locked) Crr field.
export function lookupCrr(tyreKey, surfaceKey) {
  const surface = SURFACES.find((s) => s.key === surfaceKey);
  const tyre = TYRE_CLASSES.find((t) => t.key === tyreKey);
  if (!surface || !tyre || tyre.baseCrr === null) return null;
  return surface.crr !== null ? surface.crr : tyre.baseCrr * surface.factor;
}

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
